/* Per-module master data configuration.
   The MasterPage component reads these to render forms, tables, filters, etc.
   Each module corresponds to a backend route /api/master/<key>. */

export const MASTER_MODULES = [
  {
    key: "hotel-profiles",
    label: "Hotel Profiles",
    icon: "Building2",
    group: "Property",
    title: "Hotel Profiles",
    subtitle: "Hotel Management",
    permission: "hotel_profiles",
    // Hotel records are managed by the platform Super Admin (ADR-WF-1 v2.1 §23).
    // Hotel Admins may edit tax/contact/operational fields on their existing
    // hotel(s) but cannot create new ones or delete them from here.
    disableCreate: true,
    disableDelete: true,
    disableImport: true,
    readOnlyBanner:
      "Hotel records are managed by the platform Super Admin. You can edit tax details, GST, contact info, timezone and status here — but new hotels must be created from the Platform Console.",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "brand", label: "Brand" },
      { key: "city", label: "City" },
      { key: "gst_number", label: "GST #", mono: true },
      { key: "status", label: "Status", badge: { active: "success", inactive: "neutral", closed: "error", onboarding: "warning" } },
    ],
    fields: [
      { key: "code", label: "Code", type: "text", required: true, mono: true, group: "Identity", readonly: true, hint: "Managed by the platform Super Admin — editable only from the Platform Console." },
      { key: "name", label: "Name", type: "text", required: true, group: "Identity" },
      { key: "brand", label: "Brand", type: "text", group: "Identity" },
      { key: "description", label: "Description", type: "textarea", group: "Identity" },
      { key: "address", label: "Address", type: "text", group: "Location" },
      { key: "city", label: "City", type: "text", group: "Location" },
      { key: "state", label: "State / Province", type: "text", group: "Location" },
      { key: "country", label: "Country", type: "text", group: "Location" },
      { key: "postal_code", label: "Postal code", type: "text", mono: true, group: "Location" },
      { key: "gst_number", label: "GST number", type: "text", mono: true, group: "Tax" },
      { key: "pan_number", label: "PAN number", type: "text", mono: true, group: "Tax" },
      { key: "contact_email", label: "Contact email", type: "email", group: "Contact" },
      { key: "contact_phone", label: "Contact phone", type: "text", mono: true, group: "Contact" },
      { key: "website", label: "Website", type: "text", group: "Contact" },
      { key: "timezone", label: "Timezone", type: "text", mono: true, default: "UTC", group: "Operations" },
      { key: "currency", label: "Currency", type: "text", mono: true, default: "USD", group: "Operations" },
      { key: "check_in_time", label: "Check-In Time", type: "time", mono: true, default: "14:00", group: "Operations", hint: "Standard check-in (hotel local). Arrivals before this are flagged Early Check-in." },
      { key: "check_out_time", label: "Check-Out Time", type: "time", mono: true, default: "11:00", group: "Operations", hint: "Standard check-out (hotel local). Departures after this are flagged Late Check-out." },
      { key: "status", label: "Status", type: "select", default: "active",
        options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "onboarding", label: "Onboarding" }, { value: "closed", label: "Closed" }],
        group: "Operations" },
    ],
    filters: [
      { key: "status", label: "Status", type: "select",
        options: [{ value: "", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "closed", label: "Closed" }] },
    ],
  },
  {
    key: "buildings", label: "Buildings", icon: "Building", group: "Property",
    title: "Buildings", subtitle: "Building Management", permission: "buildings",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "hotel_id", label: "Hotel", lookup: "hotel-profiles", lookupBy: "id" },
      { key: "floors_count", label: "Floors", align: "right" },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles", required: true },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "floors_count", label: "Floors count", type: "number", default: 0 },
    ],
    filters: [{ key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" }],
  },
  {
    key: "floors", label: "Floors", icon: "Layers", group: "Property",
    title: "Floors", subtitle: "Floor Management", permission: "floors",
    columns: [
      { key: "floor_number", label: "#", align: "right", width: "60px" },
      { key: "floor_name", label: "Name" },
      { key: "building_id", label: "Building", lookup: "buildings" },
      { key: "hotel_id", label: "Hotel", lookup: "hotel-profiles" },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles", required: true },
      { key: "building_id", label: "Building", type: "lookup", lookup: "buildings", required: true },
      { key: "floor_number", label: "Floor number", type: "number", required: true },
      { key: "floor_name", label: "Floor name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
    ],
    filters: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "building_id", label: "Building", type: "lookup", lookup: "buildings" },
    ],
  },
  {
    key: "room-types", label: "Room Types", icon: "BedDouble", group: "Property",
    title: "Room Types", subtitle: "Room Type Management", permission: "room_types",
    columns: [
      { key: "code", label: "Code", mono: true, width: "120px" },
      { key: "name", label: "Name" },
      { key: "occupancy", label: "Occupancy", align: "right", width: "100px" },
      { key: "base_rate", label: "Base rate", align: "right", money: true },
      { key: "amenities", label: "Amenities", chips: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "occupancy", label: "Max occupancy", type: "number", default: 2 },
      { key: "base_rate", label: "Base rate", type: "number", step: "0.01", default: 0, money: true },
      { key: "amenities", label: "Amenities (comma-separated)", type: "chips" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "rooms", label: "Rooms", icon: "DoorOpen", group: "Property",
    title: "Rooms", subtitle: "Room Management", permission: "rooms",
    columns: [
      { key: "room_number", label: "Room #", mono: true, width: "100px" },
      { key: "room_type_id", label: "Type", lookup: "room-types" },
      { key: "building_id", label: "Building", lookup: "buildings" },
      { key: "floor_id", label: "Floor", lookup: "floors", lookupField: "floor_name" },
      { key: "operational_status", label: "Operational",
        badge: { available: "success", occupied: "info", out_of_service: "error" } },
      { key: "housekeeping_status", label: "Housekeeping",
        badge: { clean: "success", dirty: "warning", in_progress: "info", inspected: "info" } },
      { key: "maintenance_status", label: "Maintenance",
        badge: { ok: "success", needs_repair: "warning", under_repair: "error" } },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles", required: true },
      { key: "building_id", label: "Building", type: "lookup", lookup: "buildings", required: true },
      { key: "floor_id", label: "Floor", type: "lookup", lookup: "floors", lookupField: "floor_name", required: true },
      { key: "room_type_id", label: "Room type", type: "lookup", lookup: "room-types", required: true },
      { key: "room_number", label: "Room number", type: "text", required: true, mono: true },
      { key: "operational_status", label: "Operational", type: "select", default: "available",
        options: ["available", "occupied", "out_of_service"].map(v => ({ value: v, label: v })) },
      { key: "housekeeping_status", label: "Housekeeping", type: "select", default: "clean",
        options: ["clean", "dirty", "in_progress", "inspected"].map(v => ({ value: v, label: v })) },
      { key: "maintenance_status", label: "Maintenance", type: "select", default: "ok",
        options: ["ok", "needs_repair", "under_repair"].map(v => ({ value: v, label: v })) },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    filters: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "building_id", label: "Building", type: "lookup", lookup: "buildings" },
      { key: "room_type_id", label: "Room type", type: "lookup", lookup: "room-types" },
      { key: "operational_status", label: "Operational", type: "select",
        options: [{ value: "", label: "All" }, { value: "available", label: "Available" }, { value: "occupied", label: "Occupied" }, { value: "out_of_service", label: "Out of service" }] },
    ],
  },
  {
    key: "designations", label: "Designations", icon: "IdCard", group: "Org",
    title: "Designations", subtitle: "Designation Management", permission: "designations",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "department_id", label: "Department" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "department_id", label: "Department (id)", type: "text", mono: true },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "services", label: "Service Catalogue", icon: "Sparkles", group: "Catalogue",
    title: "Service Catalogue", subtitle: "Services & add-ons", permission: "services",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "price", label: "Price", align: "right", money: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "text",
        placeholder: "e.g. Spa, Laundry, Restaurant, Rental" },
      { key: "price", label: "Price", type: "number", step: "0.01", default: 0 },
      { key: "description", label: "Description", type: "textarea" },
    ],
    filters: [],
  },
  {
    key: "asset-categories", label: "Asset Categories", icon: "Tag", group: "Assets",
    title: "Asset Categories", subtitle: "Asset taxonomy", permission: "asset_categories",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "assets", label: "Asset Register", icon: "Boxes", group: "Assets",
    title: "Asset Register", subtitle: "All physical assets", permission: "assets",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "category_id", label: "Category", lookup: "asset-categories" },
      { key: "serial_number", label: "Serial", mono: true },
      { key: "location", label: "Location" },
      { key: "condition", label: "Condition", badge: { new: "success", good: "success", fair: "warning", poor: "error", scrap: "error" } },
      { key: "status", label: "Status", badge: { in_service: "success", in_repair: "warning", retired: "neutral", lost: "error" } },
      { key: "purchase_cost", label: "Cost", align: "right", money: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "category_id", label: "Category", type: "lookup", lookup: "asset-categories" },
      { key: "code", label: "Asset code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "serial_number", label: "Serial number", type: "text", mono: true },
      { key: "location", label: "Location", type: "text" },
      { key: "purchase_date", label: "Purchase date", type: "date" },
      { key: "purchase_cost", label: "Purchase cost", type: "number", step: "0.01", default: 0 },
      { key: "current_value", label: "Current value", type: "number", step: "0.01" },
      { key: "condition", label: "Condition", type: "select", default: "good",
        options: ["new", "good", "fair", "poor", "scrap"].map(v => ({ value: v, label: v })) },
      { key: "status", label: "Status", type: "select", default: "in_service",
        options: ["in_service", "in_repair", "retired", "lost"].map(v => ({ value: v, label: v })) },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    filters: [
      { key: "category_id", label: "Category", type: "lookup", lookup: "asset-categories" },
      { key: "status", label: "Status", type: "select",
        options: [{ value: "", label: "All" }, ...["in_service", "in_repair", "retired", "lost"].map(v => ({ value: v, label: v }))] },
      { key: "condition", label: "Condition", type: "select",
        options: [{ value: "", label: "All" }, ...["new", "good", "fair", "poor", "scrap"].map(v => ({ value: v, label: v }))] },
    ],
    docs: true,
  },
  {
    key: "inventory-categories", label: "Inventory Categories", icon: "Tag", group: "Inventory",
    title: "Inventory Categories", subtitle: "Inventory taxonomy", permission: "inventory_categories",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "inventory-items", label: "Inventory Items", icon: "Package", group: "Inventory",
    title: "Inventory Items", subtitle: "SKU master", permission: "inventory_items",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "category_id", label: "Category", lookup: "inventory-categories" },
      { key: "unit", label: "Unit", mono: true, width: "80px" },
      { key: "reorder_level", label: "Reorder", align: "right" },
      { key: "standard_cost", label: "Cost", align: "right", money: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "category_id", label: "Category", type: "lookup", lookup: "inventory-categories" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "unit", label: "Unit (pcs, kg, l...)", type: "text", default: "pcs", mono: true },
      { key: "reorder_level", label: "Reorder level", type: "number", default: 0 },
      { key: "standard_cost", label: "Standard cost", type: "number", step: "0.01", default: 0 },
      { key: "description", label: "Description", type: "textarea" },
    ],
    filters: [{ key: "category_id", label: "Category", type: "lookup", lookup: "inventory-categories" }],
  },
  {
    key: "vendors", label: "Vendors", icon: "Truck", group: "Procurement",
    title: "Vendors", subtitle: "Vendor master", permission: "vendors",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "contact_person", label: "Contact" },
      { key: "contact_phone", label: "Phone", mono: true },
      { key: "gst_number", label: "GST #", mono: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "gst_number", label: "GST #", type: "text", mono: true },
      { key: "pan_number", label: "PAN #", type: "text", mono: true },
      { key: "contact_person", label: "Contact person", type: "text" },
      { key: "contact_email", label: "Email", type: "email" },
      { key: "contact_phone", label: "Phone", type: "text", mono: true },
      { key: "address", label: "Address", type: "textarea" },
      { key: "payment_terms", label: "Payment terms", type: "text" },
    ],
    docs: true,
  },
  {
    key: "payment-methods", label: "Payment Methods", icon: "Wallet", group: "Finance",
    title: "Payment Methods", subtitle: "Accepted methods", permission: "payment_methods",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type", badge: { cash: "success", card: "info", upi: "info", wallet: "info", bank_transfer: "warning", other: "neutral" } },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "type", label: "Type", type: "select", default: "cash",
        options: ["cash", "card", "upi", "wallet", "bank_transfer", "other"].map(v => ({ value: v, label: v })) },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "tax-configs", label: "Tax Configuration", icon: "Percent", group: "Finance",
    title: "Tax Configuration", subtitle: "Tax rates & rules", permission: "tax_configs",
    columns: [
      { key: "code", label: "Code", mono: true, width: "120px" },
      { key: "name", label: "Name" },
      { key: "rate", label: "Rate %", align: "right" },
      { key: "type", label: "Type", badge: { gst: "info", vat: "info", service: "warning", other: "neutral" } },
      { key: "inclusive", label: "Inclusive", boolean: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "rate", label: "Rate (%)", type: "number", step: "0.01", required: true },
      { key: "type", label: "Type", type: "select", default: "gst",
        options: ["gst", "vat", "service", "other"].map(v => ({ value: v, label: v })) },
      { key: "inclusive", label: "Inclusive of price?", type: "boolean", default: false },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "billing-settings", label: "Billing & Tax", icon: "Receipt", group: "Pricing",
    title: "Billing & Tax Settings", subtitle: "GST slabs · late checkout policy", permission: "hotel_profiles",
    custom: true, columns: [], fields: [],
  },
  {
    key: "rate-plans", label: "Rate Plans", icon: "BadgeDollarSign", group: "Pricing",
    title: "Rate Plans", subtitle: "Pricing strategies", permission: "rate_plans",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "room_type_id", label: "Room type", lookup: "room-types" },
      { key: "base_rate", label: "Base", align: "right", money: true },
      { key: "meal_plan", label: "Meal plan", mono: true, width: "90px" },
      { key: "valid_from", label: "From", mono: true },
      { key: "valid_to", label: "To", mono: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "room_type_id", label: "Room type", type: "lookup", lookup: "room-types" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "base_rate", label: "Base rate", type: "number", step: "0.01", required: true, money: true },
      { key: "adult_rate", label: "Adult rate", type: "number", step: "0.01", money: true },
      { key: "child_rate", label: "Child rate", type: "number", step: "0.01", money: true },
      { key: "extra_bed_rate", label: "Extra bed", type: "number", step: "0.01", money: true },
      { key: "meal_plan", label: "Meal plan", type: "select", default: "EP",
        options: [{ value: "EP", label: "EP — Room only" }, { value: "CP", label: "CP — Breakfast included" }, { value: "MAP", label: "MAP — Breakfast + one meal" }, { value: "AP", label: "AP — All meals" }] },
      { key: "valid_from", label: "Valid from", type: "date" },
      { key: "valid_to", label: "Valid to", type: "date" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "costume-catalogue", label: "Costume Catalogue", icon: "Shirt", group: "Pool",
    title: "Swimming Costume Catalogue", subtitle: "SKU master for pool inventory", permission: "costume_catalogue",
    columns: [
      { key: "code", label: "Code", mono: true, width: "180px" },
      { key: "name", label: "Name" },
      { key: "gender", label: "Gender", badge: { male: "info", female: "info", boys: "info", girls: "info" } },
      { key: "size", label: "Size", mono: true, width: "80px" },
      { key: "rental_price", label: "Rental", align: "right", money: true },
      { key: "sale_price", label: "Sale", align: "right", money: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "gender", label: "Gender", type: "select", required: true,
        options: ["male", "female", "boys", "girls"].map(v => ({ value: v, label: v })) },
      { key: "size", label: "Size", type: "select", required: true,
        options: ["XS", "S", "M", "L", "XL", "XXL"].map(v => ({ value: v, label: v })) },
      { key: "rental_price", label: "Rental price", type: "number", step: "0.01", default: 0, money: true },
      { key: "sale_price", label: "Sale price", type: "number", step: "0.01", default: 0, money: true },
      { key: "description", label: "Description", type: "textarea" },
    ],
    filters: [
      { key: "gender", label: "Gender", type: "select",
        options: [{ value: "", label: "All" }, ...["male", "female", "boys", "girls"].map(v => ({ value: v, label: v }))] },
      { key: "size", label: "Size", type: "select",
        options: [{ value: "", label: "All" }, ...["XS", "S", "M", "L", "XL", "XXL"].map(v => ({ value: v, label: v }))] },
    ],
  },
  {
    key: "costume-assets", label: "Costume Assets", icon: "QrCode", group: "Pool",
    title: "Swimming Costume Asset Register", subtitle: "Individual costumes (one row = one physical item)", permission: "costume_assets",
    columns: [
      { key: "costume_id", label: "Costume ID", mono: true, width: "160px" },
      { key: "catalogue_id", label: "Catalogue", lookup: "costume-catalogue" },
      { key: "status", label: "Status", badge: { available: "success", rented: "info", washing: "warning", retired: "neutral", lost: "error" } },
      { key: "condition", label: "Condition", badge: { new: "success", good: "success", fair: "warning", poor: "error", scrap: "error" } },
      { key: "total_rentals", label: "Rentals", align: "right" },
      { key: "revenue_generated", label: "Revenue", align: "right", money: true },
      { key: "last_rental_date", label: "Last rental", mono: true },
    ],
    fields: [
      { key: "hotel_id", label: "Hotel", type: "lookup", lookup: "hotel-profiles" },
      { key: "catalogue_id", label: "Catalogue item", type: "lookup", lookup: "costume-catalogue", required: true },
      { key: "costume_id", label: "Costume ID (QR payload)", type: "text", required: true, mono: true },
      { key: "qr_code", label: "QR code URL", type: "text", mono: true },
      { key: "purchase_date", label: "Purchase date", type: "date" },
      { key: "purchase_cost", label: "Purchase cost", type: "number", step: "0.01", default: 0 },
      { key: "condition", label: "Condition", type: "select", default: "good",
        options: ["new", "good", "fair", "poor", "scrap"].map(v => ({ value: v, label: v })) },
      { key: "status", label: "Status", type: "select", default: "available",
        options: ["available", "rented", "washing", "retired", "lost"].map(v => ({ value: v, label: v })) },
    ],
    filters: [
      { key: "catalogue_id", label: "Catalogue", type: "lookup", lookup: "costume-catalogue" },
      { key: "status", label: "Status", type: "select",
        options: [{ value: "", label: "All" }, ...["available", "rented", "washing", "retired", "lost"].map(v => ({ value: v, label: v }))] },
    ],
    docs: true,
  },

  // ===== PMS-03 Phase A — Financial Master Data =====
  {
    key: "financial-accounts", label: "Chart of Accounts", icon: "BookOpen", group: "Financial",
    title: "Chart of Accounts", subtitle: "Financial accounts (assets, liabilities, equity, revenue, expense)",
    permission: "financial_accounts",
    columns: [
      { key: "code", label: "Code", mono: true, width: "180px" },
      { key: "name", label: "Name" },
      { key: "account_type", label: "Type",
        badge: { asset: "info", liability: "warning", equity: "neutral", revenue: "success", expense: "error" } },
      { key: "account_subtype", label: "Subtype", mono: true },
      { key: "normal_balance", label: "Normal", badge: { debit: "info", credit: "success" } },
      { key: "tax_relevant", label: "Tax", boolean: true },
      { key: "is_system", label: "System", boolean: true },
    ],
    fields: [
      { key: "code", label: "Account code", type: "text", required: true, mono: true, group: "Identity" },
      { key: "name", label: "Name", type: "text", required: true, group: "Identity" },
      { key: "description", label: "Description", type: "textarea", group: "Identity" },
      { key: "account_type", label: "Account type", type: "select", required: true, default: "revenue",
        options: ["asset", "liability", "equity", "revenue", "expense"].map(v => ({ value: v, label: v })),
        group: "Classification" },
      { key: "account_subtype", label: "Subtype", type: "text", mono: true, group: "Classification" },
      { key: "normal_balance", label: "Normal balance", type: "select", required: true, default: "credit",
        options: [{ value: "debit", label: "Debit" }, { value: "credit", label: "Credit" }],
        group: "Classification" },
      { key: "parent_account_code", label: "Parent account (code)", type: "text", mono: true, group: "Classification" },
      { key: "gl_export_code", label: "GL export code", type: "text", mono: true, group: "External" },
      { key: "currency_code", label: "Currency", type: "text", mono: true, default: "INR", group: "External" },
      { key: "is_control_account", label: "Control account", type: "boolean", default: false, group: "Flags" },
      { key: "tax_relevant", label: "Tax relevant", type: "boolean", default: false, group: "Flags" },
      { key: "is_system", label: "System account (undeletable)", type: "boolean", default: false, group: "Flags" },
      { key: "hotel_id", label: "Hotel (blank = global)", type: "lookup", lookup: "hotel-profiles", group: "Scope" },
    ],
    filters: [
      { key: "account_type", label: "Type", type: "select",
        options: [{ value: "", label: "All" }, ...["asset", "liability", "equity", "revenue", "expense"].map(v => ({ value: v, label: v }))] },
      { key: "normal_balance", label: "Normal", type: "select",
        options: [{ value: "", label: "All" }, { value: "debit", label: "Debit" }, { value: "credit", label: "Credit" }] },
    ],
  },
  {
    key: "revenue-centres", label: "Revenue Centres", icon: "Landmark", group: "Financial",
    title: "Revenue Centres", subtitle: "Operational centres that produce revenue (Rooms, Restaurant, Bar, Pool …)",
    permission: "revenue_centres",
    columns: [
      { key: "code", label: "Code", mono: true, width: "140px" },
      { key: "name", label: "Name" },
      { key: "department_code", label: "Department", mono: true },
      { key: "gl_export_code", label: "GL Export", mono: true },
    ],
    fields: [
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "department_code", label: "Department code", type: "text", mono: true },
      { key: "gl_export_code", label: "GL export code", type: "text", mono: true },
      { key: "hotel_id", label: "Hotel (blank = global)", type: "lookup", lookup: "hotel-profiles" },
    ],
    filters: [],
  },
  {
    key: "revenue-categories", label: "Revenue Categories", icon: "Tag", group: "Financial",
    title: "Revenue Categories",
    subtitle: "Analytical grouping of revenue streams (Accommodation, Food, Beverage, Taxes, Discounts …)",
    permission: "revenue_categories",
    columns: [
      { key: "code", label: "Code", mono: true, width: "160px" },
      { key: "name", label: "Name" },
      { key: "category_type", label: "Type",
        badge: { revenue: "success", tax: "warning", discount: "info", adjustment: "neutral",
                 payment: "info", refund: "error", penalty: "error" } },
      { key: "default_account_code", label: "Default Account", mono: true },
    ],
    fields: [
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category_type", label: "Category type", type: "select", required: true, default: "revenue",
        options: ["revenue", "tax", "discount", "adjustment", "payment", "refund", "penalty"].map(v => ({ value: v, label: v })) },
      { key: "default_account_code", label: "Default account code", type: "text", mono: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "hotel_id", label: "Hotel (blank = global)", type: "lookup", lookup: "hotel-profiles" },
    ],
    filters: [
      { key: "category_type", label: "Type", type: "select",
        options: [{ value: "", label: "All" }, ...["revenue", "tax", "discount", "adjustment", "payment", "refund", "penalty"].map(v => ({ value: v, label: v }))] },
    ],
  },
  {
    key: "posting-templates", label: "Posting Templates", icon: "FileCode2", group: "Financial",
    title: "Posting Templates",
    subtitle: "Business-event → double-entry mapping consumed by the Posting Engine",
    permission: "posting_templates",
    columns: [
      { key: "code", label: "Code", mono: true, width: "200px" },
      { key: "name", label: "Name" },
      { key: "event_name", label: "Business Event", mono: true },
      { key: "tax_treatment", label: "Tax",
        badge: { none: "neutral", inclusive: "info", exclusive: "warning" } },
      { key: "workflow_code", label: "Workflow", mono: true },
      { key: "is_system", label: "System", boolean: true },
    ],
    fields: [
      { key: "code", label: "Template code", type: "text", required: true, mono: true, group: "Identity" },
      { key: "name", label: "Name", type: "text", required: true, group: "Identity" },
      { key: "event_name", label: "Business event name", type: "text", required: true, mono: true, group: "Identity" },
      { key: "description", label: "Description", type: "textarea", group: "Identity" },
      { key: "tax_code", label: "Tax code (from tax-configs)", type: "text", mono: true, group: "Tax" },
      { key: "tax_treatment", label: "Tax treatment", type: "select", default: "none",
        options: [{ value: "none", label: "None" }, { value: "inclusive", label: "Inclusive" }, { value: "exclusive", label: "Exclusive" }],
        group: "Tax" },
      { key: "discount_supported", label: "Supports discount", type: "boolean", default: false, group: "Behaviour" },
      { key: "workflow_code", label: "Workflow (approval)", type: "text", mono: true, group: "Behaviour" },
      { key: "rule_codes", label: "Rule codes", type: "chips", mono: true, group: "Behaviour" },
      { key: "required_dimensions", label: "Required dimensions", type: "chips", mono: true, group: "Behaviour" },
      { key: "is_system", label: "System template (undeletable)", type: "boolean", default: false, group: "Flags" },
      { key: "hotel_id", label: "Hotel (blank = global)", type: "lookup", lookup: "hotel-profiles", group: "Scope" },
    ],
    filters: [
      { key: "tax_treatment", label: "Tax", type: "select",
        options: [{ value: "", label: "All" }, { value: "none", label: "None" }, { value: "inclusive", label: "Inclusive" }, { value: "exclusive", label: "Exclusive" }] },
    ],
  },
  {
    key: "financial-calendars", label: "Financial Calendars", icon: "CalendarClock", group: "Financial",
    title: "Financial Calendars",
    subtitle: "Business-day cutover, fiscal year and period definition per property",
    permission: "financial_calendars",
    columns: [
      { key: "code", label: "Code", mono: true, width: "160px" },
      { key: "name", label: "Name" },
      { key: "business_day_start_hour", label: "Cutover (Hr)", align: "right" },
      { key: "fiscal_year_start_month", label: "FY Start (Mo)", align: "right" },
      { key: "period_type", label: "Period", mono: true },
      { key: "timezone", label: "Timezone", mono: true },
      { key: "is_default", label: "Default", boolean: true },
    ],
    fields: [
      { key: "code", label: "Code", type: "text", required: true, mono: true },
      { key: "name", label: "Name", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "business_day_start_hour", label: "Business day start — hour (0-23)", type: "number", default: 3 },
      { key: "business_day_start_minute", label: "Business day start — minute (0-59)", type: "number", default: 0 },
      { key: "fiscal_year_start_month", label: "Fiscal year start month (1-12)", type: "number", default: 4 },
      { key: "period_type", label: "Period type", type: "select", default: "monthly",
        options: [{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "4-4-5", label: "4-4-5 (future)" }] },
      { key: "timezone", label: "Timezone", type: "text", mono: true, default: "UTC" },
      { key: "is_default", label: "Default calendar", type: "boolean", default: false },
      { key: "hotel_id", label: "Hotel (blank = global)", type: "lookup", lookup: "hotel-profiles" },
    ],
    filters: [
      { key: "period_type", label: "Period", type: "select",
        options: [{ value: "", label: "All" }, { value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }] },
    ],
  },
];

export const MASTER_GROUPS = [
  { label: "Property", icon: "Building2" },
  { label: "Org", icon: "Users" },
  { label: "Catalogue", icon: "Sparkles" },
  { label: "Assets", icon: "Boxes" },
  { label: "Inventory", icon: "Package" },
  { label: "Procurement", icon: "Truck" },
  { label: "Finance", icon: "Wallet" },
  { label: "Financial", icon: "Landmark" },
  { label: "Pricing", icon: "BadgeDollarSign" },
  // NOTE: The "Pool" group is intentionally NOT listed here — Pool items
  // (Costume Catalogue / Costume Assets) surface as a dedicated top-level
  // sidebar section under Foundation groups when the Pool service is on.
];

export const findModule = (key) => MASTER_MODULES.find((m) => m.key === key);
